import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import {
  findPlaceholders,
  type TriggerTemplateCatalog,
} from "@/shared/domain/triggers";
import {
  Combobox,
  Field,
  Select,
  TextInput,
  type SelectItem,
} from "@/shared/foundation/primitives";

import styles from "./invocationSpec.module.css";

/**
 * What a row needs to know about the value it edits. An action's parameter
 * and an agent's variable both fit: a name, whether it must be given, what
 * applies when it is not, and what it is for.
 */
export interface ParameterRowParameter {
  name: string;
  required: boolean;
  default?: unknown;
  description?: string | null;
}

export interface ParameterRowProps {
  /**
   * Where the values live, a record by name: e.g. `targets.0.spec.parameters`
   * or `targets.0.variables`.
   */
  name: string;
  parameter: ParameterRowParameter;
  /** Present on a trigger: the paths a value may reference. */
  templateCatalog?: TriggerTemplateCatalog;
  /** What is wrong with the value, e.g. a required one left empty. */
  error?: string;
}

type ValueSource = "literal" | "variable";

const SOURCE_ITEMS: readonly SelectItem<ValueSource>[] = [
  { value: "literal", label: "Fixed value" },
  { value: "variable", label: "Trigger variable" },
];

/** The path a value is exactly one placeholder for, or null. */
function placeholderPath(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  const found = findPlaceholders(trimmed);
  return found.length === 1 &&
    found[0].start === 0 &&
    found[0].end === trimmed.length
    ? found[0].name
    : null;
}

function displayText(value: unknown, parameter: ParameterRowParameter): string {
  if (typeof value === "string") {
    return value;
  }
  if (value !== undefined) {
    return JSON.stringify(value);
  }
  const fallback = parameter.default;
  if (fallback === undefined || fallback === null) {
    return "";
  }
  return typeof fallback === "string" ? fallback : JSON.stringify(fallback);
}

/**
 * One declared parameter of an action, or variable of an agent.
 *
 * A row shows what would apply — the declared default until someone types —
 * but only a value that was set is held by the form, so one left alone, or
 * cleared, follows the default whenever it runs. On a trigger the value is either fixed or a
 * trigger variable, picked from the paths the checked changes expose.
 */
export function ParameterRow({
  name,
  parameter,
  templateCatalog,
  error,
}: ParameterRowProps) {
  const { control, setValue } = useFormContext();
  // Which control the row offers while nothing is written yet. A source the
  // person picked outlives an empty value, so the field does not flip back.
  const [picked, setPicked] = React.useState<ValueSource | null>(null);
  const parameters = useWatch({ control, name }) as Record<string, unknown>;
  const value = parameters[parameter.name];
  const isSet = value !== undefined;
  const variablePath = placeholderPath(value);
  const source: ValueSource =
    picked ?? (variablePath === null ? "literal" : "variable");
  const editable = !isSet || typeof value === "string";

  const write = (next: string | undefined) => {
    const { [parameter.name]: _removed, ...rest } = parameters;
    setValue(
      name,
      next === undefined ? rest : { ...rest, [parameter.name]: next },
      {
        shouldDirty: true,
      },
    );
  };

  const variablePaths: readonly string[] = (templateCatalog?.paths ?? [])
    .filter((entry) => !entry.container)
    .map((entry) => entry.path);

  return (
    <div
      className={styles.row}
      data-testid={`parameter-${parameter.name}`}
      data-set={isSet}
      data-source={source}
    >
      <Field.Root className={styles.rowFields} invalid={error !== undefined}>
        <div className={styles.parameterHeader}>
          <span className={styles.parameterName}>{parameter.name}</span>
          <span className={styles.parameterRequirement}>
            {parameter.required ? "Required" : "Optional"}
          </span>
        </div>
        {typeof parameter.description === "string" && (
          <Field.Description>{parameter.description}</Field.Description>
        )}

        <div className={styles.parameterInputs}>
          {templateCatalog !== undefined && editable && (
            <Select<ValueSource>
              items={SOURCE_ITEMS}
              value={source}
              onValueChange={(next) => {
                setPicked(next);
                // The old value cannot mean anything under the new source, so
                // the row goes back to showing the declared default.
                write(undefined);
              }}
              aria-label={`Value source for ${parameter.name}`}
              size="lg"
              className={styles.parameterSource}
            />
          )}
          <div className={styles.parameterValue}>
            {source === "variable" && editable ? (
              <Combobox<string>
                items={variablePaths}
                value={variablePath}
                onValueChange={(path) => {
                  write(path === null ? "" : `{{ ${path} }}`);
                }}
                autoHighlight
                size="lg"
              >
                <Combobox.TriggerInput
                  placeholder="Search trigger variables"
                  aria-label={`Variable for ${parameter.name}`}
                />
                <Combobox.Content>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(path: string) => (
                      <Combobox.Item key={path} value={path} />
                    )}
                  </Combobox.List>
                </Combobox.Content>
              </Combobox>
            ) : (
              <Field.Control
                render={<TextInput spellCheck={false} size="lg" />}
                value={displayText(value, parameter)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  write(event.target.value);
                }}
                readOnly={!editable}
                aria-label={parameter.name}
              />
            )}
          </div>
        </div>
        <Field.Error match>{error}</Field.Error>
      </Field.Root>
    </div>
  );
}
